import { type SavedCopy, type SavePreview, type SaveResult, saveReplenishmentInstruments } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Alert, Badge, Button, Checkbox, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, useState, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import { dayLabel } from './dates';
import { TYPE_LABEL } from './status';
import { Lines } from './tables';

const n = (v: number | null | undefined): string => (v == null ? '—' : formatCount(v, { maxDecimals: 0 }));

type Line = SavePreview['lines'][number];

type Props = {
    preview: SavePreview;
    objectiveId?: string;
    period: { start: string | null; end: string | null };
    onClose: () => void;
    onDone: (result: SaveResult) => void;
};

const readError = (e: unknown): string => {
    const err = e as { error?: { message?: string }; message?: string };
    return err?.error?.message ?? err?.message ?? 'Something went wrong';
};

const savedOn = (s: SavedCopy | null): string => (s?.savedAt ? `saved ${dayLabel(s.savedAt.slice(0, 10))}` : 'saved');

const RANK: Record<Line['match'], number> = { NEW: 0, CHANGED: 1, SAME: 2 };

const periodLabel = (p: { start: string; end: string } | null | undefined): string => (!p ? '' : p.start === p.end ? dayLabel(p.start) : `${dayLabel(p.start)} → ${dayLabel(p.end)}`);

/**
 * Every Save comes here first: each line against what this objective already saved, with a
 * tick to leave any line out. A line saved for this period with the same number is left as
 * it is; a changed one replaces its saved copy by default (or keeps it). A line of another day
 * is a new line, saved alongside what that day saved. A saved line the run no
 * longer proposes is kept or withdrawn. The plan's numbers are not touched either way.
 */
const SaveReviewSheet: FC<Props> = ({ preview, objectiveId, period, onClose, onDone }) => {
    const lines = useMemo(() => [...preview.lines].sort((a, b) => RANK[a.match] - RANK[b.match] || a.sku.localeCompare(b.sku)), [preview]);
    const count = (m: Line['match']): number => lines.filter((l) => l.match === m).length;
    const [picked, setPicked] = useState<Set<string>>(() => new Set(lines.filter((l) => l.match !== 'SAME').map((l) => l.instrumentId)));
    const [replace, setReplace] = useState<Set<string>>(() => new Set(lines.filter((l) => l.saved && l.match === 'CHANGED').map((l) => l.saved!.id)));
    const [withdraw, setWithdraw] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const toggle = (set: Set<string>, id: string, on: boolean): Set<string> => {
        const next = new Set(set);
        if (on) next.add(id);
        else next.delete(id);
        return next;
    };

    const what = (r: { sku: string; type: Line['type']; itemName: string | null; vendorName: string | null }): ReactElement => <Lines lines={[{ text: r.itemName ?? r.sku, strong: true }, { text: '', sub: `${TYPE_LABEL[r.type] ?? r.type}${r.itemName && r.itemName !== r.sku ? ` · for ${r.sku}` : ''}${r.vendorName ? ` · ${r.vendorName}` : ''}` }]} />;

    const status = (l: Line): ReactElement => {
        const s = l.saved;
        if (l.match === 'NEW' || !s) return <Badge size="sm" variant="light" color="grape" tt="none">new</Badge>;
        if (l.match === 'SAME') return <Lines lines={[{ text: 'already saved', strong: true, sub: `${savedOn(s)} · same number` }]} />;
        const delta = l.qty - s.qty;
        const sub = `${n(s.qty)} → ${n(l.qty)}${Math.abs(delta) < 0.001 ? '' : ` (${delta > 0 ? '+' : ''}${n(delta)})`}`;
        return <Lines lines={[{ text: 'changed since saved', strong: true, tone: 'orange', sub }]} />;
    };

    const columns = useMemo<DataSheetColumn<Line>[]>(
        () => [
            {
                key: 'keep',
                header: '',
                width: 44,
                render: (_v, l): ReactElement => <Checkbox size="xs" aria-label="Save this line" disabled={l.match === 'SAME'} checked={l.match !== 'SAME' && picked.has(l.instrumentId)} onChange={(e) => setPicked((cur) => toggle(cur, l.instrumentId, e.currentTarget.checked))} />,
            },
            { key: 'what', header: 'Line', width: 240, render: (_v, l): ReactElement => what(l) },
            { key: 'day', header: 'Day', width: 90, render: (_v, l): ReactElement => <Text size="sm">{dayLabel(l.placeBy)}</Text> },
            { key: 'qty', header: 'Plan now', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm" fw={700}>{n(l.qty)}</Text> },
            { key: 'status', header: 'Saved', width: 200, render: (_v, l): ReactElement => status(l) },
            { key: 'done', header: 'Done so far', width: 100, align: 'right', render: (_v, l): ReactElement => <Text size="sm">{n(l.saved?.doneQty)}</Text> },
            {
                key: 'pick',
                header: '',
                width: 200,
                render: (_v, l): ReactElement | null => {
                    const s = l.saved;
                    if (!s || l.match !== 'CHANGED' || !picked.has(l.instrumentId)) return null;
                    return <SegmentedControl size="xs" value={replace.has(s.id) ? 'new' : 'keep'} onChange={(v) => setReplace((cur) => toggle(cur, s.id, v === 'new'))} data={[{ value: 'new', label: 'Take new' }, { value: 'keep', label: 'Keep saved' }]} />;
                },
            },
        ],
        [picked, replace],
    );
    const droppedColumns = useMemo<DataSheetColumn<SavedCopy>[]>(
        () => [
            { key: 'what', header: 'Line', width: 240, render: (_v, s): ReactElement => what(s) },
            { key: 'day', header: 'Day', width: 90, render: (_v, s): ReactElement => <Text size="sm">{dayLabel(s.placeBy)}</Text> },
            { key: 'saved', header: 'Saved', width: 120, align: 'right', render: (_v, s): ReactElement => <Lines lines={[{ text: n(s.qty), strong: true, sub: savedOn(s) }]} /> },
            { key: 'done', header: 'Done so far', width: 110, align: 'right', render: (_v, s): ReactElement => <Text size="sm">{n(s.doneQty)}</Text> },
            {
                key: 'pick',
                header: '',
                width: 200,
                render: (_v, s): ReactElement => <SegmentedControl size="xs" value={withdraw.has(s.id) ? 'withdraw' : 'keep'} onChange={(v) => setWithdraw((cur) => toggle(cur, s.id, v === 'withdraw'))} data={[{ value: 'keep', label: 'Keep saved' }, { value: 'withdraw', label: 'Withdraw' }]} />,
            },
        ],
        [withdraw],
    );
    const chosen = lines.filter((l) => l.match !== 'SAME' && picked.has(l.instrumentId));
    const replacing = chosen.filter((l) => l.saved && l.match === 'CHANGED' && replace.has(l.saved.id));
    const saving = chosen.filter((l) => !(l.match === 'CHANGED' && !(l.saved && replace.has(l.saved.id))));
    const writes = saving.length + withdraw.size;
    const confirm = async (): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            onDone(await saveReplenishmentInstruments(saving.map((l) => l.instrumentId), objectiveId, period, { replaceSavedIds: replacing.map((l) => l.saved!.id), withdrawSavedIds: [...withdraw] }));
        } catch (e) {
            setError(readError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title="Save plan"
            subtitle={`${period.start && period.end ? `for ${periodLabel({ start: period.start, end: period.end })} · ` : ''}${count('NEW')} new · ${count('CHANGED')} changed · ${count('SAME')} already saved${preview.dropped.length ? ` · ${preview.dropped.length} no longer proposed` : ''}`}
            footer={
                <Group justify="space-between" p="sm">
                    <Text size="xs" c="dimmed">
                        Saved lines are a record to compare with actuals. The plan's numbers don't change either way.
                    </Text>
                    <Group gap="xs">
                        <Button size="xs" variant="default" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button size="xs" color="grape" onClick={() => void confirm()} loading={busy} disabled={writes === 0}>
                            {writes === 0 ? 'Nothing to save' : `Save ${saving.length}${replacing.length ? ` · replacing ${replacing.length}` : ''}${withdraw.size ? ` · withdraw ${withdraw.size}` : ''}`}
                        </Button>
                    </Group>
                </Group>
            }
        >
            <Stack gap="md" p="md">
                {error && (
                    <Alert color="red" withCloseButton onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                <DataSheet<Line> data={lines} columns={columns} getRowKey={(l) => l.instrumentId} rowHeight={48} bare hideHeaderBar />
                {preview.dropped.length > 0 && (
                    <Stack gap={6}>
                        <Text size="sm" fw={700}>
                            Saved, but the run no longer proposes them ({preview.dropped.length})
                        </Text>
                        <Text size="xs" c="dimmed">
                            Usually stock or in-production moved since the save. Check Done so far before you withdraw.
                        </Text>
                        <DataSheet<SavedCopy> data={preview.dropped} columns={droppedColumns} getRowKey={(s) => s.id} rowHeight={48} bare hideHeaderBar />
                    </Stack>
                )}
            </Stack>
        </ExplainWindow>
    );
};

export default SaveReviewSheet;
